import { Hit } from 'algoliasearch';
import {
  ContentfulLocale,
  DEFAULT_LOCALE,
} from '@core/constants/locales.constants';
import { ArticleCardProps, LinkListItemProps } from '@stadionhq-v3/base-ui';
import { ArrowRight, Article, Gallery, Play } from '@components/icons';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import templateConfig from '@templateConfig';
import {
  contentfulLocaleToLocale,
  localeToContentfulLocale,
} from '@utils/locales.utils';
import { ContentfulImage } from '@stadionhq-v3/contentful-ui';
import { fullPathToArray } from '@utils/strings.utils';

const fallbackImage = (
  <img
    src="/images/search_fallback.png"
    style={{ objectFit: 'cover', objectPosition: 'packcenter' }}
    loading="lazy"
    width={176}
    height={176}
    alt="Search Fallback"
  />
);

export const pagesTransformer = (
  item: Hit,
  locale: ContentfulLocale
): LinkListItemProps => {
  const defaultContentfulLocale = localeToContentfulLocale(DEFAULT_LOCALE);

  const fullPath =
    item?.fields?.fullPath?.[locale] ||
    item?.fields?.fullPath?.[defaultContentfulLocale];
  const title =
    item?.fields?.headerBlock?.[locale]?.fields?.title?.[locale] ||
    item?.fields?.metaTitle?.[locale] ||
    item?.fields?.metaTitle?.[locale]?.fields?.shortTitle?.[locale] ||
    item?.fields?.headerBlock?.[defaultContentfulLocale]?.fields?.title?.[
      defaultContentfulLocale
    ] ||
    item?.fields?.metaTitle?.[defaultContentfulLocale] ||
    item?.fields?.metaTitle?.[defaultContentfulLocale]?.fields?.shortTitle?.[
      defaultContentfulLocale
    ];

  return {
    iconAfter: <ArrowRight />,
    title,
    url: generateFullPathname({
      route: templateConfig.navigation.routes.CONTENT,
      params: {
        content: fullPath ? fullPathToArray(fullPath) : [],
      },
      locale: contentfulLocaleToLocale(locale),
    }),
  };
};

export const newsTransformer = (
  item: Hit,
  locale: ContentfulLocale
): ArticleCardProps => {
  const defaultContentfulLocale = localeToContentfulLocale(DEFAULT_LOCALE);

  const imageUrl =
    item?.fields?.image?.[locale]?.fields?.image?.[locale]?.fields?.file?.[
      locale
    ]?.url ||
    item?.fields?.image?.[defaultContentfulLocale]?.fields?.image?.[
      defaultContentfulLocale
    ]?.fields?.file?.[defaultContentfulLocale]?.url;
  const focalPoint =
    item?.fields?.image?.[locale]?.fields?.focalPoint?.[locale]?.focalPoint ||
    item?.fields?.image?.[defaultContentfulLocale]?.fields?.focalPoint?.[
      defaultContentfulLocale
    ]?.focalPoint;
  const category =
    item?.fields?.category?.[locale] ||
    item?.fields?.category?.[defaultContentfulLocale];
  const publishDate =
    item?.fields?.publishDate?.[locale] ||
    item?.fields?.publishDate?.[defaultContentfulLocale];
  const format =
    item?.fields?.format?.[locale] ||
    item?.fields?.format?.[defaultContentfulLocale];

  return {
    title: item?.fields?.articleTitle?.[defaultContentfulLocale],
    ...(category && {
      category: {
        category: category?.fields?.category?.[defaultContentfulLocale],
        slug: generateFullPathname({
          locale: contentfulLocaleToLocale(locale),
          route: templateConfig.navigation.routes.NEWS_CATEGORY,
          params: {
            category: category?.fields?.slug?.[defaultContentfulLocale],
          },
        }),
      },
    }),
    cardIcon: {
      icon:
        format === 'News' ? (
          <Article />
        ) : format === 'Video' ? (
          <Play />
        ) : format === 'Gallery' ? (
          <Gallery />
        ) : (
          <></>
        ),
    },
    ...(publishDate && {
      publishDate: { date: publishDate, format: "DD MMM 'YY" },
    }),

    image: imageUrl ? (
      <ContentfulImage
        alt={
          item?.fields?.image?.[locale]?.fields?.title?.[locale] ||
          item?.fields?.image?.[defaultContentfulLocale]?.fields?.title?.[
            defaultContentfulLocale
          ]
        }
        url={`https:${imageUrl}`}
        width={
          item?.fields?.image?.[locale]?.fields?.image?.[locale]?.fields
            ?.file?.[locale]?.details?.image?.width ||
          item?.fields?.image?.[defaultContentfulLocale]?.fields?.image?.[
            defaultContentfulLocale
          ]?.fields?.file?.[defaultContentfulLocale]?.details?.image?.width
        }
        height={
          item?.fields?.image?.[locale]?.fields?.image?.[locale]?.fields
            ?.file?.[locale]?.details?.image?.height ||
          item?.fields?.image?.[defaultContentfulLocale]?.fields?.image?.[
            defaultContentfulLocale
          ]?.fields?.file?.[defaultContentfulLocale]?.details?.image?.height
        }
        {...(focalPoint && {
          focalPoint: {
            x: focalPoint?.x,
            y: focalPoint?.y,
          },
        })}
      />
    ) : (
      fallbackImage
    ),
    slug: generateFullPathname({
      route: templateConfig.navigation.routes.ARTICLE,
      params: {
        article:
          item?.fields?.slug?.[locale] ||
          item?.fields?.slug?.[defaultContentfulLocale],
      },
      locale: contentfulLocaleToLocale(locale),
    }),
  };
};

export const videosTransformer = (
  item: Hit,
  locale: ContentfulLocale
): ArticleCardProps => {
  const defaultContentfulLocale = localeToContentfulLocale(DEFAULT_LOCALE);
  const fields = item?.fields;
  const videoImage =
    fields?.video?.[locale]?.thumbnailImageUrl ||
    fields?.video?.[defaultContentfulLocale]?.thumbnailImageUrl;

  return {
    title: fields?.title?.[locale] || fields?.title?.[defaultContentfulLocale],
    publishDate: {
      date:
        fields?.video?.[locale]?.date ||
        fields?.video?.[defaultContentfulLocale]?.date,
      format: "DD MMM 'YY",
    },
    cardIcon: { icon: <Play /> },
    image: videoImage ? (
      <img
        src={videoImage}
        alt={
          fields?.title?.[locale] || fields?.title?.[defaultContentfulLocale]
        }
        style={{ objectFit: 'cover', objectPosition: 'center' }}
        loading="lazy"
        width={176}
        height={176}
      />
    ) : (
      fallbackImage
    ),
    slug: generateFullPathname({
      route: templateConfig.navigation.routes.VIDEO_DETAIL,
      params: {
        slug: fields?.slug?.[locale] || fields?.slug?.[defaultContentfulLocale],
      },
      locale: contentfulLocaleToLocale(locale),
    }),
  };
};
